import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Spinner } from '@/src/components/ui/spinner'
import { me } from '@/src/api/user'
import { getOperation } from '@/src/api/operation'
import { userCanManageOrganizationSettings } from '@/src/lib/permissionHelpers'
import { OperationForm } from '@/src/components/manage/forms/OperationForm'

export const Route = createFileRoute('/_manage/manage/organization/operations_/$operationId/edit')({
  component: OrgOperationEditPage,
})

function OrgOperationEditPage() {
  const navigate = useNavigate()
  const { operationId } = useParams({ from: '/_manage/manage/organization/operations_/$operationId/edit' })

  const { data: user, isLoading: isUserLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => me(),
  })

  const permissions: string[] = (user?.role?.permissions as string[]) ?? []
  const isAdmin = permissions.includes('*')
  const canManageOrg = userCanManageOrganizationSettings(permissions, isAdmin)
  const orgId = user?.organization?.id

  const { data: operation, isLoading: isOperationLoading } = useQuery({
    queryKey: ['operation', operationId],
    queryFn: () => getOperation({ data: { id: operationId } }),
    enabled: !!operationId,
  })

  useEffect(() => {
    if (!isUserLoading && !isOperationLoading) {
      if (!canManageOrg || !orgId) {
        navigate({ to: '/not-permitted' })
        return
      }

      // Additional check: ensure the operation's drawing belongs to the organization
      // Note: getOperation already validates access permissions
    }
  }, [
    isUserLoading,
    isOperationLoading,
    canManageOrg,
    orgId,
    navigate,
  ])

  if (isUserLoading || isOperationLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    )
  }

  if (!canManageOrg || !orgId) return null

  if (!operation) return null

  const goBack = () => {
    navigate({
      to: '/manage/drawings/$id',
      params: { id: operation.drawingId },
      search: { tab: 'data' },
    })
  }

  const handleSuccess = () => {
    goBack()
  }

  const handleCancel = () => {
    goBack()
  }

  return (
    <div className="flex flex-col h-full">
      <AdminToolbar>
        <AdminToolbar.Left>
          <AdminToolbar.Title>
            Edit Operation: {operation.name}
          </AdminToolbar.Title>
        </AdminToolbar.Left>
      </AdminToolbar>

      <div className="flex-1 min-h-0">
        <div className="h-full overflow-auto p-6">
          <OperationForm
            drawingId={operation.drawingId}
            operation={operation}
            onCancel={handleCancel}
            onSuccess={handleSuccess}
          />
        </div>
      </div>
    </div>
  )
}
